import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSimpleEmpresa } from '@/hooks/useSimpleEmpresa';
import { 
  KeyIcon, 
  BuildingOfficeIcon, 
  EnvelopeIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import toastService from '@/utils/toastService';

/**
 * Componente para que los clientes soliciten acceso a una empresa existente
 */
export const SolicitarAcceso = () => {
  const navigate = useNavigate();
  const { unirseEmpresa } = useSimpleEmpresa();
  
  const [method, setMethod] = useState(''); // 'invitation' o 'request'
  const [formData, setFormData] = useState({
    codigoInvitacion: '',
    codigoEmpresa: '',
    mensaje: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Manejar unión por código de invitación
  const handleInvitationJoin = async (e) => {
    e.preventDefault();
    if (!formData.codigoInvitacion.trim()) {
      setError('Debes ingresar un código de invitación');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await unirseEmpresa(formData.codigoInvitacion.trim().toUpperCase());
      toastService.success('¡Te has unido exitosamente a la empresa!');
      navigate('/dashboard');
    } catch (error) {
      setError(error.message || 'Código de invitación inválido o expirado');
      toastService.error('Error al unirse a la empresa');
    } finally {
      setLoading(false);
    }
  };

  // Manejar solicitud de acceso (futuro)
  const handleAccessRequest = async (e) => {
    e.preventDefault();
    // TODO: Implementar lógica de solicitud de acceso
    toastService.info('Función de solicitud de acceso en desarrollo');
  };

  if (!method) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <div className="max-w-2xl mx-auto text-center">
          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center justify-center mb-6">
              <BuildingOfficeIcon className="h-12 w-12 text-green-500 mr-3" />
              <h1 className="text-3xl font-bold text-white">Revvly</h1>
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">
              Acceso a Empresa Existente
            </h2>
            <p className="text-gray-300 text-lg">
              ¿Cómo quieres acceder al sistema?
            </p>
          </div>

          {/* Opciones */}
          <div className="space-y-6">
            {/* Opción: Código de Invitación */}
            <div 
              onClick={() => setMethod('invitation')}
              className="bg-gray-800 border-2 border-gray-700 hover:border-green-500 rounded-xl p-6 cursor-pointer transition-all duration-300 hover:transform hover:scale-105"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
                    <KeyIcon className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="ml-4 text-left">
                  <h3 className="text-lg font-semibold text-white">
                    Tengo un Código de Invitación
                  </h3>
                  <p className="text-gray-300 text-sm">
                    El taller me envió un código para acceder directamente
                  </p>
                </div>
              </div>
            </div>

            {/* Opción: Solicitar Acceso */}
            <div 
              onClick={() => setMethod('request')}
              className="bg-gray-800 border-2 border-gray-700 hover:border-blue-500 rounded-xl p-6 cursor-pointer transition-all duration-300 hover:transform hover:scale-105"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                    <EnvelopeIcon className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="ml-4 text-left">
                  <h3 className="text-lg font-semibold text-white">
                    Solicitar Acceso
                  </h3>
                  <p className="text-gray-300 text-sm">
                    Buscar una empresa y solicitar acceso como cliente
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Botón para volver */}
          <div className="mt-8">
            <button
              onClick={() => navigate('/select-user-type')}
              className="text-gray-400 hover:text-white transition-colors"
            >
              ← Volver a selección de tipo
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <BuildingOfficeIcon className="h-8 w-8 text-green-500 mr-3" />
            <h1 className="text-2xl font-bold text-white">Revvly</h1>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">
            {method === 'invitation' ? 'Código de Invitación' : 'Solicitar Acceso'}
          </h2>
          <p className="text-gray-300">
            {method === 'invitation' 
              ? 'Ingresa el código que te proporcionó el taller'
              : 'Busca la empresa y solicita acceso como cliente'
            }
          </p>
        </div>

        {/* Formulario */}
        <div className="bg-gray-800 rounded-xl p-6">
          {method === 'invitation' ? (
            <form onSubmit={handleInvitationJoin}>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Código de Invitación
                </label>
                <input
                  type="text"
                  value={formData.codigoInvitacion}
                  onChange={(e) => setFormData({ ...formData, codigoInvitacion: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Ej: ABC123XYZ"
                  disabled={loading}
                />
              </div>

              {error && (
                <div className="mb-4 bg-red-900/30 border border-red-500/30 rounded-lg p-3">
                  <div className="flex items-center">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
                    <p className="text-red-400 text-sm">{error}</p>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !formData.codigoInvitacion.trim()}
                className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    Unirse a Empresa
                  </>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleAccessRequest}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Código de Empresa
                </label>
                <input
                  type="text"
                  value={formData.codigoEmpresa}
                  onChange={(e) => setFormData({ ...formData, codigoEmpresa: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ej: taller-central"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Mensaje (opcional)
                </label>
                <textarea
                  value={formData.mensaje}
                  onChange={(e) => setFormData({ ...formData, mensaje: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Cuéntanos por qué quieres acceder..."
                />
              </div>

              <button
                type="submit"
                disabled={!formData.codigoEmpresa.trim()}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-2 px-4 rounded-lg transition-colors"
              >
                Enviar Solicitud
              </button>
            </form>
          )}

          {/* Botón para volver */}
          <div className="mt-6 text-center">
            <button
              onClick={() => setMethod('')}
              className="text-gray-400 hover:text-white transition-colors text-sm"
            >
              ← Cambiar método de acceso
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SolicitarAcceso;